import React, { ReactElement } from 'react'

import { Roundel } from '@mindfulchefuk/design-system/Roundel/Roundel'
import { Box } from '@mindfulchefuk/design-system/layout/Box'
import { Text } from '@mindfulchefuk/design-system/Text'
import { theme } from '@mindfulchefuk/design-system'
import { Flex } from '@mindfulchefuk/design-system/layout/Flex'

import { ResetButton } from '@mindfulchefuk/design-system/Button'
import { Icon } from '../Icon'

type TNumberBasedStepper = {
  currentStep: number
  currentStepName?: string
  setCurrentStep: (step: number) => void
  onClickTracking?: (currentStep: string, clickedStep: string) => void
  disabled: boolean
  steps: { label: string; step: number; forceDisableStep?: boolean }[]
  testId?: string
  forceLastStepToBeEnabledIfActive?: boolean
}

export const NumberBasedStepper = ({
  currentStep,
  currentStepName,
  setCurrentStep,
  onClickTracking,
  disabled,
  steps,
  testId,
  forceLastStepToBeEnabledIfActive = false, // a hack added to be able to make design happy
}: TNumberBasedStepper): ReactElement => {
  const indexOfCurrentStep = steps.findIndex(
    (step) => step.step === currentStep
  )
  if (indexOfCurrentStep === -1) {
    throw new Error(`Step ${currentStep} not found in steps`)
  }
  return (
    <Flex position="relative" mb={32}>
      <Box
        position="absolute"
        borderBottomWidth={2}
        borderStyle="dashed"
        top={20}
        width="100%"
        color="aubergine-200"
      />
      <Box
        position="absolute"
        borderBottomWidth={2}
        borderStyle="dashed"
        backgroundColor="white"
        top={20}
        maxWidth="100%" // hack used to avoid width going to 150% on last step
        width={`${((indexOfCurrentStep + 1) / (steps.length - 1)) * 100}%`}
        color="aubergine-200"
      />
      <Box
        position="absolute"
        borderBottomWidth={2}
        borderStyle="solid"
        backgroundColor="white"
        top={20}
        width={`${(indexOfCurrentStep / (steps.length - 1)) * 100}%`}
        color={disabled ? 'aubergine-200' : 'aubergine'}
        zIndex={0}
      />
      <Flex
        as="ol"
        justifyContent="space-between"
        alignItems="flex-start"
        p={0}
        w="100%"
        data-testid={testId}
        listStyleType="none"
      >
        {steps.map(({ label, step, forceDisableStep }, index) => {
          const isFirstStep = index === 0
          const isLastStep = index === steps.length - 1
          const isLastStepActive = currentStep === steps[steps.length - 1].step
          const visited = currentStep >= step

          const active = currentStep === step

          const forceLastStepToBeEnabled =
            isLastStep && isLastStepActive && forceLastStepToBeEnabledIfActive
          const isDisabled =
            (disabled || forceDisableStep || !visited) &&
            !forceLastStepToBeEnabled

          return (
            <Flex
              as="li"
              position="relative"
              key={label}
              color={isDisabled ? 'aubergine-200' : 'aubergine'}
            >
              {isFirstStep && (
                <Box
                  position="absolute"
                  backgroundColor="white"
                  width="50%"
                  height="100%"
                />
              )}
              {isLastStep && (
                <Box
                  position="absolute"
                  backgroundColor="white"
                  width="50%"
                  height="100%"
                  left="50%"
                />
              )}
              <Box zIndex={1}>
                <ResetButton
                  data-testid={label}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    if (
                      onClickTracking &&
                      currentStepName &&
                      currentStep !== step
                    )
                      onClickTracking(currentStepName, label)
                    setCurrentStep(step)
                  }}
                >
                  <Flex as="span" position="relative">
                    <Roundel
                      filled={active || visited}
                      size={40}
                      fontSize={24}
                      disabled={isDisabled}
                      color={
                        isDisabled
                          ? theme.colors['aubergine-200']
                          : theme.colors.aubergine
                      }
                      disableOpacity
                    >
                      {index + 1}
                      {active && (
                        <Box position="absolute" right={-40} top="50%">
                          <Box
                            borderTopWidth={2}
                            borderColor="aubergine"
                            position="absolute"
                            left={-10}
                            width={10}
                          />
                          <Box
                            position="relative"
                            bottom={10}
                            pl={1}
                            bg="white"
                          >
                            <Icon type="delivery" color="aubergine" />
                          </Box>
                        </Box>
                      )}
                    </Roundel>
                  </Flex>

                  <Text
                    variant="body-sm-500-caps"
                    mt={8}
                    color={isDisabled ? 'aubergine-200' : 'aubergine'}
                    whiteSpace="nowrap"
                    zIndex={1}
                  >
                    {label}
                  </Text>
                </ResetButton>
              </Box>
            </Flex>
          )
        })}
      </Flex>
    </Flex>
  )
}
